/**
 * Recharts Fluent UI Theme Configuration
 * Maps Fluent UI design tokens to Recharts styling
 */

import { tokens } from '@fluentui/react-components';
import { dataVisualizationTokens } from '../../../tokens';

/**
 * Get Fluent UI color for chart elements
 */
export const getFluentChartColors = () => {
  return {
    // Categorical colors for different data series
    categorical: [
      dataVisualizationTokens.qualitative.slot1,
      dataVisualizationTokens.qualitative.slot2,
      dataVisualizationTokens.qualitative.slot3,
      dataVisualizationTokens.qualitative.slot4,
      dataVisualizationTokens.qualitative.slot5,
      dataVisualizationTokens.qualitative.slot6,
      dataVisualizationTokens.qualitative.slot7,
      dataVisualizationTokens.qualitative.slot8,
      dataVisualizationTokens.qualitative.slot9,
      dataVisualizationTokens.qualitative.slot10,
    ],
    
    // Semantic colors for sentiment data
    semantic: {
      positive: dataVisualizationTokens.semantic.positive,
      neutral: dataVisualizationTokens.semantic.neutral,
      negative: dataVisualizationTokens.semantic.negative,
      success: tokens.colorPaletteGreenBackground3,
      warning: tokens.colorPaletteYellowBackground3,
      error: tokens.colorPaletteRedBackground3,
    },
    
    // Sequential colors for heatmaps/gradients
    sequential: {
      light: dataVisualizationTokens.sequential.cornflower.tint50,
      medium: dataVisualizationTokens.sequential.cornflower.primary,
      dark: dataVisualizationTokens.sequential.cornflower.shade30,
    },
    
    // UI elements
    ui: {
      background: tokens.colorNeutralBackground1,
      foreground: tokens.colorNeutralForeground1,
      border: tokens.colorNeutralStroke1,
      gridLine: tokens.colorNeutralStroke2,
      text: tokens.colorNeutralForeground1,
      textSecondary: tokens.colorNeutralForeground2,
    },
  };
};

/**
 * Default Recharts styling using Fluent UI tokens
 */
export const fluentRechartsStyle = {
  cartesianGrid: {
    stroke: tokens.colorNeutralStroke2,
    strokeDasharray: '3 3',
  },
  
  xAxis: {
    stroke: tokens.colorNeutralStroke1,
    tick: { fill: tokens.colorNeutralForeground2, fontSize: 12 },
    axisLine: { stroke: tokens.colorNeutralStroke1 },
  },
  
  yAxis: {
    stroke: tokens.colorNeutralStroke1,
    tick: { fill: tokens.colorNeutralForeground2, fontSize: 12 },
    axisLine: { stroke: tokens.colorNeutralStroke1 },
  },
  
  tooltip: {
    contentStyle: {
      backgroundColor: tokens.colorNeutralBackground1,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      borderRadius: tokens.borderRadiusMedium,
      padding: '8px 12px',
      boxShadow: tokens.shadow16,
    },
    labelStyle: {
      color: tokens.colorNeutralForeground1,
      fontWeight: tokens.fontWeightSemibold,
      marginBottom: '4px',
    },
    itemStyle: {
      color: tokens.colorNeutralForeground2,
      fontSize: '12px',
    },
  },
  
  legend: {
    iconType: 'circle' as const,
    wrapperStyle: {
      paddingTop: '16px',
      fontSize: '12px',
      color: tokens.colorNeutralForeground1, // Standard black for better readability
    },
    // Text styling for legend items
    formatter: (value: string) => value,
  },
};

/**
 * Get color by token path (e.g., "qualitative.slot1")
 */
export const getColorByTokenPath = (path: string): string => {
  const parts = path.split('.');
  
  if (parts[0] === 'qualitative' && parts[1]) {
    return dataVisualizationTokens.qualitative[parts[1] as keyof typeof dataVisualizationTokens.qualitative];
  }
  
  if (parts[0] === 'semantic' && parts[1]) {
    return dataVisualizationTokens.semantic[parts[1] as keyof typeof dataVisualizationTokens.semantic];
  }
  
  if (parts[0] === 'sequential' && parts[1]) {
    // Return the cornflower primary as default for sequential
    return dataVisualizationTokens.sequential.cornflower.primary;
  }
  
  // Default fallback
  return dataVisualizationTokens.qualitative.slot1;
};

/**
 * Animation configuration using Fluent motion tokens
 */
export const fluentAnimationConfig = {
  duration: 300, // Fluent UI standard duration
  easing: 'ease-in-out',
};
